import { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";

import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import MeasureConfigList from "@plannel/components/modal/MeasureConfigList";
import ToggleButton from "@plannel/components/ToggleButton";
import measureService from "@plannel/services/data/measure-service";
import p13nService from "@plannel/services/system/p13n-service";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";

/*
 * DP Workbench 차트 설정.
 * 셸 = BaseConfigModal, 본문 = MeasureConfigList (표준 — 규약: saas-web/.claude/skills/config-modal/SKILL.md).
 *
 * 저장 포맷(기존 유지): content.chart = { [measureCd]: { name, type, activeFlg, chartType } }
 *   · **현재 토글(AMT/QTY)로 필터된 측정치만** 저장한다 → 차트는 한 번에 한 단위만 표시.
 *   · z_p13n pageCd = CodeUtil.dpP13N.DP_WORKBENCH_ETC
 */
// 차트 타입 4종 — 앱 전역 규약. DASHED 는 WorkbenchUtil 이 toggleType.DASHED 로 매핑한다.
const CHART_TYPES = ["LINE", "BAR", "AREA", "DASHED"];

const ChartConfigModal = (props) => {
  const { t, close, open, content, modalResultData, additionalMeasures, viewName } = props;
  const [measures, setMeasures] = useState([]);   // 전체 측정치 행
  const [draft, setDraft] = useState({});          // { [measureCd]: { activeFlg, chartType } }
  const [toggledMeasure, setToggledMeasure] = useState(CodeUtil.toggleType.AMT);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const pageCd = viewName === "DP_WORKBENCH" ? CodeUtil.dpP13N.DP_WORKBENCH_ETC : CodeUtil.dpP13N.DP_CHANNEL_WORKBENCH_ETC;
  const { snackBarMessageColor } = ColorUtil;

  const buildRows = (measureDatas) => {
    const saved = content.chart ?? {};
    // 저장된 설정의 단위(AMT/QTY)로 토글 초기화
    if (Object.keys(saved).length > 0) {
      const first = Object.keys(saved)[0];
      if (saved[first].type?.toLowerCase() === CodeUtil.toggleType.QTY) {
        setToggledMeasure(CodeUtil.toggleType.QTY);
      }
    }

    const rows = measureDatas.map((measure) => ({
      measureCd: measure.measureCd,
      name: measure.name,
      valueTypeCd: measure.valueTypeCd,
    }));
    setMeasures(rows);

    const next = {};
    rows.forEach((r) => {
      const s = saved[r.measureCd];
      next[r.measureCd] = {
        activeFlg: s ? s.activeFlg : false,
        chartType: s ? s.chartType : "BAR",
      };
    });
    setDraft(next);
  };

  const retrieveMeasures = () => {
    const isChannelPage = pageCd === CodeUtil.dpP13N.DP_CHANNEL_WORKBENCH_ETC;
    const request = isChannelPage
      ? measureService.getChannelMeasure({ isActive: true })
      : measureService.getAll({ isActive: true });

    const channelMeasure = CodeUtil.channelOnlyMeasureCds;

    request
      .then((response) => {
        const dbMeasures = response.data?.results ?? [];
        const filteredMeasures = [];
        for (const m of dbMeasures) {
          if (!isChannelPage && channelMeasure.includes(m.measureCd)) {
            continue;
          }
          filteredMeasures.push(m);
        }

        const dbCds = new Set(filteredMeasures.map((m) => m.measureCd));
        const extra = (additionalMeasures ?? []).filter((m) => !dbCds.has(m.measureCd));
        buildRows([...filteredMeasures, ...extra]);
      })
      .catch((e) => {
        debug(e);
      });
  };

  useEffect(() => {
    if (open) {
      retrieveMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 현재 단위 토글로 필터된 측정치만 화면·저장 대상
  const filtered = useMemo(() => {
    const unit = toggledMeasure === CodeUtil.toggleType.QTY ? CodeUtil.toggleType.QTY : CodeUtil.toggleType.AMT;
    return measures.filter((m) => m.valueTypeCd?.toLowerCase() === unit);
  }, [measures, toggledMeasure]);

  // 측정치별 색 — 차트와 같은 함수(WorkbenchUtil.chartColorMap)로, 지금 켜진 측정치 기준으로 계산한다
  //   = 저장하면 차트에 나올 색. 꺼진 행은 "켜면 받을 색"을 보여 준다.
  const colorByKey = useMemo(() => {
    const active = filtered.filter((m) => draft[m.measureCd]?.activeFlg).map((m) => m.measureCd);
    const map = WorkbenchUtil.chartColorMap(active);
    filtered.forEach((m) => {
      if (!map[m.measureCd]) {
        map[m.measureCd] = WorkbenchUtil.chartColorMap([...active, m.measureCd])[m.measureCd];
      }
    });
    return map;
  }, [filtered, draft]);

  const listMeasures = useMemo(
    () => filtered.map((m) => ({ key: m.measureCd, label: GridUtils.gridValueL10N(m.name), hasActive: true, color: colorByKey[m.measureCd] })),
    [filtered, colorByKey],
  );

  const handleConfirm = () => {
    const results = {};
    filtered.forEach((m) => {
      const d = draft[m.measureCd];
      if (d?.activeFlg) {
        results[m.measureCd] = {
          name: m.name,
          type: m.valueTypeCd,
          activeFlg: d.activeFlg,
          chartType: d.chartType,
        };
      }
    });

    content.chart = results;
    const params = { pageCd, configObj: content };

    p13nService.upsert(params)
      .then((response) => {
        if (response.status === 200) {
          modalResultData({ content, reload: true });
        }
      })
      .catch((e) => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .finally(() => {
        close(false);
      });
  };

  const handleClose = () => {
    close(false);
  };

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("dpChartConfig")}
        onSave={handleConfirm}
      >
        {/* AMT/QTY 토글은 검색창과 같은 줄(우측)에 */}
        <MeasureConfigList
          open={open}
          measures={listMeasures}
          draft={draft}
          setDraft={setDraft}
          chartTypes={CHART_TYPES}
          toolbarRight={
            <ToggleButton toggleType="measure" initialValue={toggledMeasure} toggleValue={setToggledMeasure}></ToggleButton>
          }
        />
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
}

export default withTranslation()(ChartConfigModal);
